import { safeContactField, safeFlowPage } from "./formFields";
export const FLOW_STEPS = {
    check_availability: ["contact"],
    test_drive: ["contact"],
    walkaround_video: ["contact"],
    sell_my_car: ["vehicle_lookup", "trimConfirmation", "mileage", "modifications", "ownership", "keys", "accidents", "condition", "startsRunsDrives", "preference", "comments", "contact"],
    financing: ["contact", "provider_handoff"]
} as const;
export type FlowName = keyof typeof FLOW_STEPS;
export const FLOW_VERSION = "1";
export const ATTEMPT_FIELD = "analyticsAttemptId";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function validAttemptId(value: unknown): string | undefined {
    return typeof value === "string" && UUID.test(value) ? value : undefined;
}
export function readAttemptId(formData: FormData): string | undefined {
    return validAttemptId(formData.get(ATTEMPT_FIELD));
}
export type FlowError = "validation" | "verification" | "request_failed" | "lookup_failed" | "timeout" | "conflict" | "api_rejected" | "network" | "receipt_failed";
export type FlowEvent = Record<string, string | number> & { event: string };

interface Dependencies {
    send: (event: FlowEvent) => void;
    allowed: () => boolean;
    uuid: () => string;
    now: () => string;
    environment: "production" | "development";
    page?: () => string;
}

/** One mounted flow/explicit restart = one attempt; retries retain its opaque identity. */
export function createFormFlow(name: FlowName, deps: Dependencies) {
    let id: string | undefined;
    let ended = false;
    let pending = false;
    let sequence = 0;
    let coverage = "complete";
    let page = "unknown";
    let lastField = "";
    let steps: readonly string[] = FLOW_STEPS[name];
    const viewed = new Set<string>();
    const completed = new Set<string>();
    const isStep = (step: string) => (FLOW_STEPS[name] as readonly string[]).includes(step);
    function emit(event: string, step: string, outcome = "", error = "", field = "") {
        if (!id || !isStep(step)) return;
        try {
            if (!deps.allowed()) { coverage = "partial"; return; }
            deps.send({
                event, event_id: deps.uuid(), event_sequence: ++sequence,
                occurred_at: deps.now(), flow_instance_id: id,
                flow_name: name, form_name: name, form_type: name,
                flow_version: FLOW_VERSION, schema_version: "1",
                step_id: step, step_index: steps.indexOf(step), step_count: steps.length,
                page_location: `https://thiscar.com${name === "sell_my_car" ? "/sell-my-car" : name === "financing" ? "/finance" : "/cars/[vin]"}`,
                flow_outcome: outcome, error_code: error,
                measurement_coverage: coverage, environment: deps.environment,
                flow_page: page, field_id: field
            });
        } catch { coverage = "partial"; } // Analytics must never prevent a submission.
    }
    function view(step: string) {
        if (!id || ended || !isStep(step) || viewed.has(step)) return;
        viewed.add(step);
        emit("tc_flow_step_view", step);
    }
    function begin(step: string) {
        if (ended || !isStep(step)) return undefined;
        if (!id) {
            try { id = validAttemptId(deps.uuid()); } catch { return undefined; }
            try { page = safeFlowPage(deps.page?.() ?? ""); } catch { page = "unknown"; }
            emit("tc_flow_start", step);
        }
        view(step);
        return id;
    }
    function complete(step: string) {
        if (!id || ended || !isStep(step) || completed.has(step)) return;
        completed.add(step);
        pending = false;
        emit("tc_flow_step_complete", step);
    }
    return {
        begin, view, complete,
        field(field: string | undefined) {
            const safe = safeContactField(field);
            if (!safe || ended) return;
            begin("contact");
            if (lastField === safe) return;
            lastField = safe;
            emit("tc_flow_field_interaction", "contact", "", "", safe);
        },
        fieldError(field: string | undefined) {
            const safe = safeContactField(field);
            if (!safe || ended) return;
            begin("contact");
            emit("tc_flow_field_error", "contact", "", "validation", safe);
        },
        configureSteps(next: readonly string[]) {
            if (next.length && next.every(isStep) && new Set(next).size === next.length) steps = [...next];
        },
        submit(step: string) {
            if (ended || pending || !isStep(step)) return undefined;
            begin(step);
            pending = true;
            emit("tc_flow_submit_attempt", step);
            return id;
        },
        error(code: FlowError, step: string) {
            if (ended || !["validation", "verification", "request_failed", "lookup_failed", "timeout", "conflict", "api_rejected", "network", "receipt_failed"].includes(code)) return;
            pending = false;
            emit("tc_flow_error", step, "", code);
        },
        finish(receipt: string | undefined, step: string, outcome: "accepted" | "ineligible" | "pending_review" = "accepted") {
            if (!id || receipt !== id || ended || !isStep(step)) return false;
            complete(step);
            emit(outcome === "accepted" ? "tc_flow_success" : "tc_flow_terminal", step, outcome);
            ended = true;
            pending = false;
            return true;
        },
        handoff() {
            if (name !== "financing" || !id || ended) return;
            complete("contact");
            view("provider_handoff");
            emit("tc_flow_handoff", "provider_handoff", "handoff");
            ended = true;
            pending = false;
        },
        attach(formData: FormData) {
            try { if (id && deps.allowed()) formData.set(ATTEMPT_FIELD, id); } catch { /* No analytics metadata. */ }
        },
        getAttemptId() {
            try { return deps.allowed() ? id : undefined; } catch { return undefined; }
        },
        reset() {
            id = undefined; ended = false; pending = false; sequence = 0; coverage = "complete";
            viewed.clear(); completed.clear();
            page = "unknown"; lastField = "";
        }
    };
}
export type FormFlow = ReturnType<typeof createFormFlow>;
